import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * The chat list's order, held still while you are reading it.
 *
 * Gordon, 23 October: the list goes by activity - the server sorts it, see
 * backend lib/chat-activity.js - but "keep the list stable while you're
 * reading (don't jump rows under the cursor on every poll)". A message
 * arriving re-sorts the list, and a row moving out from under the pointer a
 * moment before a click is how somebody opens the wrong conversation.
 *
 * So while the pointer is over the list, something in it has the keyboard,
 * or a finger touched or scrolled it in the last few seconds, the rows keep
 * the order they had: their contents (the badge, the last line) update in
 * place, and anybody new is put at the top, where new activity goes anyway.
 * The moment that stops, the list takes the server's order - and useFlip
 * below slides the rows there rather than letting them jump.
 */

const sameOrder = (a, b) => a.length === b.length && a.every((k, i) => k === b[i]);

/**
 * `items` in the order they were last shown while `hold` is true, and in
 * their own order otherwise. `keyOf` names an item.
 */
export function useHeldOrder(items, keyOf, hold) {
  const [order, setOrder] = useState(() => items.map(keyOf));
  useEffect(() => {
    const keys = items.map(keyOf);
    setOrder((was) => {
      let next = keys;
      if (hold) {
        const present = new Set(keys);
        const kept = was.filter((k) => present.has(k));
        const fresh = keys.filter((k) => !was.includes(k));
        next = [...fresh, ...kept];
      }
      return sameOrder(next, was) ? was : next;
    });
    // keyOf is a plain function of an item and does not change meaning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, hold]);
  const byKey = new Map(items.map((i) => [keyOf(i), i]));
  const placed = order.filter((k) => byKey.has(k));
  // Anything the held order has not met yet - the render before the effect
  // above catches up - goes at the top, as the effect will put it.
  const unplaced = items.map(keyOf).filter((k) => !placed.includes(k));
  return [...unplaced, ...placed].map((k) => byKey.get(k));
}

/**
 * Whether the list should hold still: the pointer over it, the keyboard in
 * it, or a touch or scroll in the last `ms`. Spread `bind` on the element.
 */
export function useReading(ms = 2500) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [touch, setTouch] = useState(false);
  const timer = useRef(null);
  const touched = () => {
    setTouch(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setTouch(false), ms);
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return {
    reading: hover || focus || touch,
    bind: {
      onPointerEnter: (e) => { if (e.pointerType === 'mouse') setHover(true); },
      onPointerLeave: (e) => { if (e.pointerType === 'mouse') setHover(false); },
      // The KEYBOARD in the list, not a click that left a row focused - or
      // the list would stay held long after the pointer had gone elsewhere.
      onFocus: (e) => {
        let keyboard = false;
        try { keyboard = e.target.matches(':focus-visible'); } catch { keyboard = false; }
        if (keyboard) setFocus(true);
      },
      onBlur: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFocus(false); },
      onTouchStart: touched,
      onScroll: touched
    }
  };
}

const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
};

/**
 * Rows that move, slid rather than jumped: FLIP. Each element inside
 * `container` carrying data-flip="<key>" is measured after every render; when
 * one has moved since the last, it is drawn back where it was and eased to
 * where it is. Nothing at all with prefers-reduced-motion.
 */
export function useFlip(container, signature) {
  const tops = useRef(new Map());
  useLayoutEffect(() => {
    const root = container.current;
    if (!root) return;
    const now = new Map();
    const still = reducedMotion() || typeof Element === 'undefined' || !Element.prototype.animate;
    for (const el of root.querySelectorAll('[data-flip]')) {
      const key = el.getAttribute('data-flip');
      const top = el.offsetTop;
      now.set(key, top);
      const was = tops.current.get(key);
      if (!still && was !== undefined && was !== top) {
        el.animate(
          [{ transform: `translateY(${was - top}px)` }, { transform: 'translateY(0)' }],
          { duration: 240, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
      }
    }
    tops.current = now;
  }, [container, signature]);
}

/*
 * THE LIST'S FILTER - All, Unread, People, Groups - remembered in this
 * browser, as the folded sections are: it is a preference about a screen.
 * Wrapped, because storage can be off or full.
 */
export const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'people', label: 'People' },
  { key: 'groups', label: 'Groups' }
];
/*
 * "@ Me" (084): only the conversations where somebody has @mentioned me and
 * I have not read it yet. Offered beside Unread once the server has
 * mentions; a server without them gets the four it always had.
 */
export const MENTIONS_FILTER = { key: 'mentions', label: '@ Me' };
export const filtersFor = (mentions) => (mentions
  ? [FILTERS[0], FILTERS[1], MENTIONS_FILTER, FILTERS[2], FILTERS[3]] : FILTERS);
const FILTER_KEY = 'jarvis.chat.filter';
export const readFilter = () => {
  try {
    const v = window.localStorage.getItem(FILTER_KEY);
    return [...FILTERS, MENTIONS_FILTER].some((f) => f.key === v) ? v : 'all';
  } catch {
    return 'all';
  }
};
export const writeFilter = (v) => {
  try { window.localStorage.setItem(FILTER_KEY, v); } catch { /* not remembered */ }
};
